<template>
    <div class="flex flex-col text-[16px] md:text-13">
        <div v-if="label" class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <p class="text-13 font-medium whitespace-pre-line" v-html="label">
            </p>
            <!-- <p v-if="note?.lastModifiedBy" class="text-11 text-right italic">
                Sidst opdateret <span v-if="note.lastModifiedDate" class="font-medium">{{ $dateFormat(note.lastModifiedDate) }}</span> af <UserInfo class="font-medium" :user-id="note.lastModifiedBy"/>
            </p> -->
        </div>
        
        <ckeditor v-if="ClassicEditor && config"
                  v-model="content"
                  :editor="ClassicEditor"
                  :config="config"
                  :disabled="disabled"/>

        <p v-if="!hideErrorIndicator && errorMessage" class="mt-1 text-11 text-red-600">
            {{ errorMessage }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, toRefs } from 'vue';
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import { useField } from 'vee-validate';

import {
    AutoLink,
    ButtonView,
    ClassicEditor,
    Alignment,
    BlockQuote,
    Bold,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    HorizontalLine,
    Indent,
    IndentBlock,
    Italic,
    Link,
    Paragraph,
    Plugin,
    RemoveFormat,
    Strikethrough,
    Underline,
    EditorConfig,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

const props = defineProps<{
  modelValue?: string,
  label?: string,
  disabled?: boolean,
  name?: string,
  hideErrorIndicator?: boolean
}>();

const { modelValue } = toRefs(props);
const emit = defineEmits(['update:modelValue']);

const field = props.name
    ? useField<string>(props.name, undefined, {
        initialValue: props.modelValue ?? '',
        validateOnValueUpdate: true,
        syncVModel: true,
        label: props.label,
    })
    : null;

const errorMessage = field?.errorMessage ?? ref<string | undefined>(undefined);

const content = computed({
    get: () => field?.value.value ?? modelValue.value ?? '',
    set: value => {
        if (field) {
            field.value.value = value;
            return;
        }

        emit('update:modelValue', value);
    },
});

const isLayoutReady = ref(false);

class ClearButton extends Plugin {
    init() {
        const editor = this.editor;
        editor.ui.componentFactory.add('clear', locale => {
            const view = new ButtonView(locale);
            view.set({
                label: 'Nulstil',
                tooltip: 'Ryd alt indhold',
                withText: false,
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16"><path d="M4.22 4.22a.75.75 0 0 1 1.06 0L8 6.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L8 9.06l-2.72 2.72a.75.75 0 1 1-1.06-1.06L6.94 8 4.22 5.28a.75.75 0 0 1 0-1.06z" fill="currentColor"/></svg>',
            });
            view.on('execute', () => {
                editor.setData('');
                content.value = '';
            });
            return view;
        });
    }
}

const config = computed<EditorConfig | null>(() => {
    if (!isLayoutReady.value) {
        return null;
    }

    return {
        toolbar: {
            items: [
                'bold',
                'italic',
                'underline',
                'strikethrough',
                'link',
                '|',
                'alignment',
                'fontSize',
                'fontFamily',
                'fontColor',
                'fontBackgroundColor',
                'removeFormat',
                '|',
                'horizontalLine',
                'outdent',
                'indent',
                '|',
                'clear',
            ],
            shouldNotGroupWhenFull: true,
        },
        plugins: [
            Alignment,
            AutoLink,
            BlockQuote,
            Bold,
            Essentials,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            GeneralHtmlSupport,
            HorizontalLine,
            Indent,
            IndentBlock,
            Italic,
            Link,
            Paragraph,
            RemoveFormat,
            Strikethrough,
            Underline,
            ClearButton,
        ],
        fontFamily: {
            supportAllValues: true,
        },
        fontSize: {
            options: ['default', 10, 12, 14, 16, 18, 20, 22, 24],
            supportAllValues: true,
        },
        htmlSupport: {
            allow: [
                {
                    name: /^.*$/,
                    styles: true,
                    attributes: true,
                    classes: true,
                },
            ],
        },
        licenseKey: 'GPL',
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
        },
    };
});

onMounted(() => setTimeout(() => {
    isLayoutReady.value = true;
}, 150));
</script>

<style lang="scss" scoped>
:global(.ck-body-wrapper) {
    // CKEditor renders balloons outside the editor element. Keep them above our
    // modal stack (which starts at 10000), while leaving notifications on top.
    --ck-z-default: 20000;
    --ck-z-panel: 20999;
    --ck-z-dialog: 30000;
}
</style>
